import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { tr } from "../lib/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const FIELDS = { hide_price: "sg.f_price", background: "sg.f_background", template: "sg.f_size", lang: "sg.f_language", both_mode: "sg.f_language" };

export function EditDialog({ s, onClose, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setText(s?.instructions || ""); }, [s]);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/social/suggestions/${s.id}/regenerate`, { instructions: text });
      const applied = [...new Set((data.applied || []).map((k) => tr(FIELDS[k])))];
      toast.success(applied.length ? tr("sg.applied", { p0: applied.join(", ") }) : tr("sg.regenerated_toast"));
      onDone(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!s} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent data-testid="sg-edit-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle>{tr("sg.edit_title")}</DialogTitle><DialogDescription>{tr("sg.edit_help")}</DialogDescription></DialogHeader>
        <textarea dir="auto" rows={4} maxLength={500} className="input w-full" value={text} onChange={(e) => setText(e.target.value)} placeholder={tr("sg.edit_placeholder")} data-testid="sg-edit-text" />
        <div className="flex justify-end gap-2">
          <button className="btn-secondary" disabled={busy} onClick={onClose} data-testid="sg-edit-cancel">{tr("sg.cancel")}</button>
          <button className="btn-primary" disabled={busy || !text.trim()} onClick={go} data-testid="sg-edit-submit">
            {busy ? <><Loader2 size={14} className="animate-spin" /> {tr("sg.working")}</> : tr("sg.regenerate")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const inOneHour = () => {
  const d = new Date(Date.now() + 3600e3);
  d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5, 0, 0);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60e3).toISOString().slice(0, 16);
};

export function ScheduleDialog({ s, onClose, onDone }) {
  const [at, setAt] = useState(inOneHour());
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (s) setAt(inOneHour()); }, [s]);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/social/suggestions/${s.id}/schedule`, { at: new Date(at).toISOString() });
      toast.success(tr("sg.scheduled_toast"));
      onDone(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!s} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent data-testid="sg-schedule-dialog" className="max-w-sm">
        <DialogHeader><DialogTitle>{tr("sg.schedule_title")}</DialogTitle><DialogDescription>{tr("sg.mock_note")}</DialogDescription></DialogHeader>
        <label className="block">
          <span className="eyebrow">{tr("sg.schedule_at")}</span>
          <input type="datetime-local" dir="ltr" className="input w-full mt-2" value={at} onChange={(e) => setAt(e.target.value)} data-testid="sg-schedule-at" />
        </label>
        <div className="flex justify-end gap-2">
          <button className="btn-secondary" disabled={busy} onClick={onClose} data-testid="sg-schedule-cancel">{tr("sg.cancel")}</button>
          <button className="btn-primary" disabled={busy || !at} onClick={go} data-testid="sg-schedule-submit">{tr("sg.save_schedule")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
